/**
 * Gösterge — KK-802, KK-803, KK-810, KK-811, KK-538.
 *
 * =============================================================================
 * BU EKRANIN CEVAPLADIĞI SORU
 * -----------------------------------------------------------------------------
 * "Bugün ne oldu ve şu anda açık riskim ne?" Serkan akşam masasına oturur;
 * ekranın ilk ekranında kaydırmadan şunları görmelidir: bugünkü ciro, kaç
 * işlem, kaç terminal ayakta, kaç iş bekliyor ve OFFLINE MARUZİYET.
 *
 * TEK RAKAM GRAFİK DEĞİLDİR (dataviz): bugünkü ciro, işlem adedi ve bekleyen
 * sayaçları KARO olarak gösterilir. Bunları grafiğe çevirmek bilgi eklemez,
 * gürültü ekler. Grafik yalnızca DAĞILIM (saatlik yoğunluk) ve KARŞILAŞTIRMA
 * (noktalar) için kullanılır.
 *
 * NOKTA SÜZGECİ: üst bardaki süzgeç ciro/işlem karolarına uygulanır (özet
 * zaten nokta kırılımı döner). Terminal, maruziyet ve fotoğraf kapsaması OKUL
 * GENELİDİR ve öyle etiketlenir — süzgecin sessizce uygulanmadığı bir sayı,
 * yanlış karar demektir. Noktalar karşılaştırması ise KK-810 gereği HER ZAMAN
 * tüm noktaları gösterir; tek noktayla karşılaştırma yapılamaz.
 * =============================================================================
 */
import { Kart, Rozet, Tutar, Uyari } from "@kantin/ui";
import { YUKLEME_ROLLERI } from "@kantin/shared";
import { MaruziyetGostergesi } from "@kantin/ui";
import {
  IstatistikKarosu,
  NoktaKarsilastirma,
  SaatlikYogunluk,
} from "@kantin/ui/grafik";
import type { NoktaOlcumu, SaatlikNokta } from "@kantin/ui/grafik";
import { aktifOturum, oturumDurumu } from "../../veri/oturum.js";
import {
  useFotoKapsama,
  useMaruziyet,
  useNoktaKarsilastirma,
  useOzet,
} from "../../veri/sorgular.js";
import type { Ozet, OzetSaatlik } from "../../veri/tipler.js";
import { gunGoster, saat } from "../../veri/bicim.js";
import { Sayfa, SayfaBasligi, SorguDurumu } from "../../ortak.js";
import { miktarGoster } from "../yonetim/ortak.js";

export function Gosterge() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const ozet = useOzet();
  const maruziyet = useMaruziyet();
  const foto = useFotoKapsama();

  const gun = ozet.data?.gun;
  // KK-810: karşılaştırma HER ZAMAN tüm noktaları kapsar (nokta_id verilmez).
  const karsilastirma = useNoktaKarsilastirma(gun, gun, null);

  const secili = seciliNoktaId
    ? ozet.data?.noktalar.find((n) => n.nokta_id === seciliNoktaId)
    : undefined;

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Gösterge"
        aciklama={
          ozet.data ? (
            <>
              {gunGoster(ozet.data.gun)} · okul saat dilimi{" "}
              <span className="font-mono">{ozet.data.saat_dilimi}</span> ·{" "}
              <span className="text-murekkep-3">
                veri {saat(new Date(ozet.dataUpdatedAt))} itibarıyla
              </span>
            </>
          ) : (
            "Bugünkü ciro, işlem trafiği ve açık risk"
          )
        }
      />

      <SorguDurumu
        yukleniyor={ozet.isLoading}
        hata={ozet.error}
        bos={false}
        iskeletSatir={6}
        yenile={() => void ozet.refetch()}
      >
        {ozet.data && (
          <>
            {/* Nokta görevlisi: sunucu her sayıyı atandığı noktalarla süzdü.
                "Okul geneli" etiketi burada YANLIŞ olur; kapsam açıkça yazılır. */}
            {ozet.data.kapsam === "nokta" && (
              <Uyari tur="bilgi">
                Bu gösterge <strong>atandığınız noktalar</strong> içindir: ciro,
                terminal, maruziyet ve bekleyen sayaçları okul genelini değil,
                sorumlu olduğunuz noktaları gösterir.
              </Uyari>
            )}

            {seciliNoktaId && (
              <Uyari tur="bilgi">
                Nokta süzgeci açık. Ciro ve işlem karoları{" "}
                <strong>{secili?.nokta_ad ?? "seçili nokta"}</strong> içindir;
                terminal, maruziyet ve fotoğraf kapsaması{" "}
                {ozet.data.kapsam === "nokta"
                  ? "atandığınız noktaların tamamı içindir"
                  : "okul genelidir"}
                .
              </Uyari>
            )}

            <Karolar ozet={ozet.data} seciliNoktaId={seciliNoktaId} />

            {/* KK-811 — gerçek risk tavan değil, tavan x offline terminaldir */}
            <section className="grid gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <h2 className="m-0 text-lg">Offline maruziyet</h2>
                {maruziyet.isLoading ? (
                  <div
                    className="h-20 animate-nabiz rounded-md bg-kagit-2"
                    role="status"
                    aria-label="Maruziyet yükleniyor"
                  />
                ) : maruziyet.error ? (
                  <Uyari tur="hata" baslik="Maruziyet okunamadı">
                    Açık risk tutarı şu anda hesaplanamıyor.
                  </Uyari>
                ) : maruziyet.data ? (
                  <>
                    <MaruziyetGostergesi
                      offlineTerminal={maruziyet.data.offline_terminal}
                      toplamTerminal={maruziyet.data.toplam_terminal}
                      acikRisk={maruziyet.data.acik_risk_tutari}
                    />
                    <p className="m-0 text-sm text-murekkep-3">
                      {maruziyet.data.aciklama} Offline nokta sayısı:{" "}
                      <span className="rakam font-mono">
                        {maruziyet.data.offline_nokta}
                      </span>
                      .
                    </p>
                  </>
                ) : null}
              </div>

              {/* KK-538 — düşük kapsama = klonlama riski yüksek */}
              <FotoKapsamaKarti
                yukleniyor={foto.isLoading}
                hata={foto.error}
                veri={foto.data}
              />
            </section>

            {/* KK-803 — teneffüs pikleri, personel planlaması için */}
            <SaatlikYogunluk veri={saatlikSeri(ozet.data.saatlik_dagilim)} />

            {/* KK-810 — noktalar karşılaştırması */}
            <section className="flex flex-col gap-3">
              <SorguDurumu
                yukleniyor={karsilastirma.isLoading}
                hata={karsilastirma.error}
                bos={
                  !karsilastirma.isLoading &&
                  (karsilastirma.data?.noktalar.length ?? 0) === 0
                }
                bosBaslik="Bugün için nokta verisi yok"
                bosAciklama="Satış noktalarında henüz işlem görünmüyor."
                iskeletSatir={4}
                yenile={() => void karsilastirma.refetch()}
              >
                <NoktaKarsilastirma
                  veri={olcumler(karsilastirma.data?.noktalar)}
                />
                <EnCokSatanlar veri={karsilastirma.data?.noktalar ?? []} />
              </SorguDurumu>
            </section>
          </>
        )}
      </SorguDurumu>
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function Karolar({
  ozet,
  seciliNoktaId,
}: {
  ozet: Ozet;
  seciliNoktaId: string | null;
}) {
  const oturum = aktifOturum(oturumDurumu());
  const secili = seciliNoktaId
    ? ozet.noktalar.find((n) => n.nokta_id === seciliNoktaId)
    : undefined;

  const ciro = secili ? secili.ciro : ozet.ciro.toplam;
  const islem = secili ? secili.islem_adedi : ozet.islem_adedi;
  const genel =
    ozet.kapsam === "nokta" ? "atandığınız noktalar" : "okul geneli";
  const kapsam = secili
    ? secili.nokta_ad
    : ozet.kapsam === "nokta"
      ? "Atandığınız noktalar"
      : "Okul geneli";

  return (
    <section
      aria-label="Bugünün özeti"
      className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      <IstatistikKarosu
        etiket="Bugünkü ciro"
        vurgu
        deger={<Tutar deger={ciro} yonsuz boy="2xl" etiket="bugünkü ciro" />}
        yardimci={
          secili ? (
            kapsam
          ) : (
            <>
              kart <Tutar deger={ozet.ciro.kart} yonsuz boy="sm" /> · nakit{" "}
              <Tutar deger={ozet.ciro.nakit} yonsuz boy="sm" />
            </>
          )
        }
      />

      <IstatistikKarosu
        etiket="İşlem adedi"
        deger={<span className="rakam font-mono">{islem}</span>}
        yardimci={
          <>
            {kapsam} · iptal{" "}
            <span className="rakam font-mono">{ozet.iptal_adedi}</span> ·
            offline satış{" "}
            <span className="rakam font-mono">{ozet.offline_satis_adedi}</span>
          </>
        }
      />

      <IstatistikKarosu
        etiket="Aktif terminal"
        deger={
          <span className="rakam font-mono">
            {ozet.terminal.cevrimici} / {ozet.terminal.aktif_tanimli}
          </span>
        }
        yardimci={
          ozet.terminal.offline > 0 ? (
            <>
              <span className="rakam font-mono">{ozet.terminal.offline}</span>{" "}
              terminal çevrimdışı — {genel}
            </>
          ) : (
            `Hepsi çevrimiçi — ${genel}`
          )
        }
      />

      <IstatistikKarosu
        etiket="Bekleyen mutabakat"
        deger={
          <span className="rakam font-mono">{ozet.bekleyen.mutabakat}</span>
        }
        yardimci={
          <>
            mutabakat bekleyen satış{" "}
            <span className="rakam font-mono">
              {ozet.bekleyen.mutabakat_bekleyen_satis}
            </span>
          </>
        }
      />

      {/* Yükleme sayacı yalnızca taleplere karar verebilen rollere gösterilir. */}
      {ozet.kapsam === "okul" &&
        oturum &&
        YUKLEME_ROLLERI.includes(oturum.personel.rol) && (
          <IstatistikKarosu
            etiket="Bekleyen yükleme onayı"
            deger={
              <span className="rakam font-mono">
                {ozet.bekleyen.yukleme_onayi}
              </span>
            }
            yardimci="Veli havale bildirimleri (KK-1210)"
          />
        )}

      <IstatistikKarosu
        etiket="Düşük stok"
        deger={
          <span className="rakam font-mono">{ozet.bekleyen.dusuk_stok}</span>
        }
        yardimci="Kritik seviyenin altındaki ürün sayısı"
      />
    </section>
  );
}

// ---------------------------------------------------------------------------

function FotoKapsamaKarti({
  yukleniyor,
  hata,
  veri,
}: {
  yukleniyor: boolean;
  hata: unknown;
  veri:
    | {
        toplam_kisi: number;
        fotografli: number;
        fotosuz: number;
        kapsama_yuzdesi: string | null;
        dusuk_kapsama: boolean;
        esik_yuzde: string;
        aciklama: string;
      }
    | undefined;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="m-0 text-lg">Fotoğraf kapsaması</h2>
      {yukleniyor ? (
        <div
          className="h-20 animate-nabiz rounded-md bg-kagit-2"
          role="status"
          aria-label="Kapsama yükleniyor"
        />
      ) : hata ? (
        <Uyari tur="hata" baslik="Kapsama okunamadı">
          Fotoğraf kapsaması şu anda hesaplanamıyor.
        </Uyari>
      ) : veri ? (
        <Kart>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="m-0 text-xs text-murekkep-3">Kapsama</p>
              <p className="rakam m-0 font-mono text-2xl font-semibold text-murekkep">
                {veri.kapsama_yuzdesi ? `%${veri.kapsama_yuzdesi}` : "—"}
              </p>
              <p className="m-0 text-sm text-murekkep-2">
                <span className="rakam font-mono">{veri.fotografli}</span> /{" "}
                <span className="rakam font-mono">{veri.toplam_kisi}</span> kişi
                · fotosuz{" "}
                <span className="rakam font-mono">{veri.fotosuz}</span>
              </p>
            </div>
            <Rozet tur={veri.dusuk_kapsama ? "uyari" : "onay"}>
              {veri.dusuk_kapsama
                ? `eşik %${veri.esik_yuzde} altında`
                : "eşik üzerinde"}
            </Rozet>
          </div>
          {veri.dusuk_kapsama && (
            <Uyari
              tur="uyari"
              baslik="Düşük fotoğraf kapsaması"
              className="mt-3"
            >
              {veri.aciklama} Kapsama artana kadar görevlinin tanıyarak
              doğrulaması tek savunma hattıdır.
            </Uyari>
          )}
        </Kart>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** KK-810: en çok satan ürün de karşılaştırmanın parçasıdır. */
function EnCokSatanlar({
  veri,
}: {
  veri: {
    nokta_id: string;
    nokta_ad: string;
    islem_adedi: number;
    iptal_adedi: number;
    en_cok_satan_urun: string | null;
    en_cok_satan_adet: string | null;
  }[];
}) {
  if (veri.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-murekkep-2">
      {veri.map((n) => (
        <li
          key={n.nokta_id}
          className="flex flex-wrap justify-between gap-2 border-b border-cetvel py-1 last:border-0"
        >
          <span className="text-murekkep">{n.nokta_ad}</span>
          <span>
            en çok satan:{" "}
            <strong className="text-murekkep">
              {n.en_cok_satan_urun ?? "—"}
            </strong>
            {n.en_cok_satan_adet && (
              <span className="rakam ml-1 font-mono">
                ({miktarGoster(n.en_cok_satan_adet)} adet)
              </span>
            )}
            <span className="ml-3 text-murekkep-3">
              iptal <span className="rakam font-mono">{n.iptal_adedi}</span>/
              <span className="rakam font-mono">{n.islem_adedi}</span>
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Veri dönüşümleri
// ---------------------------------------------------------------------------

/**
 * Özet yalnızca İŞLEM GÖRMÜŞ saatleri döner. Eksik saatler grafikte "veri yok"
 * gibi değil, "o saatte işlem olmadı" gibi okunmalıdır; bu yüzden ilk ve son
 * dolu saat arasındaki boşluklar SIFIRLA doldurulur.
 */
function saatlikSeri(dagilim: OzetSaatlik[]): SaatlikNokta[] {
  if (dagilim.length === 0) return [];
  const saatler = dagilim.map((d) => d.saat);
  const bas = Math.min(...saatler);
  const bit = Math.max(...saatler);
  const harita = new Map(dagilim.map((d) => [d.saat, d.adet]));
  const seri: SaatlikNokta[] = [];
  for (let s = bas; s <= bit; s += 1) {
    seri.push({
      saat: `${String(s).padStart(2, "0")}:00`,
      islem: harita.get(s) ?? 0,
    });
  }
  return seri;
}

function olcumler(
  satirlar:
    | {
        nokta_id: string;
        nokta_ad: string;
        ciro: string;
        sepet_ortalamasi: string | null;
        islem_adedi: number;
      }[]
    | undefined,
): NoktaOlcumu[] {
  return (satirlar ?? []).map((n) => ({
    nokta_id: n.nokta_id,
    nokta_ad: n.nokta_ad,
    ciro: n.ciro,
    // İşlem yoksa sepet ortalaması null döner; grafik için "0.00" gösterilir.
    sepet_ortalama: n.sepet_ortalamasi ?? "0.00",
    islem: n.islem_adedi,
  }));
}
